import { Goal, GoalSortingMode, ID, ImpactLevel } from '@griply/types';

import { getCompletionPercentage } from './get-completion-percentage';
import { getGoalCompletion } from './get-goal-completion';
import { sortItems } from './sort-items';

export type SortGoalsOptions<T extends Goal = Goal> = {
  allGoals: T[];
  sortMode?: GoalSortingMode;
  customOrder?: ID[] | null;
  invertStartDateSorting?: boolean;
  lifeAreaOrder?: Map<ID, number>;
};

const compareByDeadline = (goalA: Goal, goalB: Goal): number => {
  const { deadline: deadlineA } = goalA;
  const { deadline: deadlineB } = goalB;
  if (!deadlineA && !deadlineB) {
    return 0;
  }
  if (!deadlineA) {
    return 1;
  }
  if (!deadlineB) {
    return -1;
  }
  return deadlineA.getTime() - deadlineB.getTime();
};

export const sortGoals = <T extends Goal>(
  goals: T[],
  {
    allGoals,
    sortMode,
    customOrder,
    invertStartDateSorting,
    lifeAreaOrder,
  }: SortGoalsOptions<T>,
): T[] => {
  if (sortMode === GoalSortingMode.Custom) {
    return sortItems(goals, customOrder ?? [], 'id');
  }

  if (sortMode === GoalSortingMode.StartDate) {
    return goals.slice().sort((goalA, goalB) => {
      const { startDate: startDateA } = goalA;
      const { startDate: startDateB } = goalB;

      if (!startDateA && !startDateB) {
        return compareByDeadline(goalA, goalB);
      }
      if (!startDateA) {
        return 1;
      }
      if (!startDateB) {
        return -1;
      }

      const dateA = invertStartDateSorting ? startDateB : startDateA;
      const dateB = invertStartDateSorting ? startDateA : startDateB;
      const diff = dateA.getTime() - dateB.getTime();
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Deadline) {
    return goals
      .slice()
      .sort(({ deadline: deadlineA }, { deadline: deadlineB }) => {
        if (!deadlineA && !deadlineB) {
          return 0;
        }
        if (!deadlineA) {
          return 1;
        }
        if (!deadlineB) {
          return -1;
        }

        return deadlineA.getTime() - deadlineB.getTime();
      });
  }

  if (sortMode === GoalSortingMode.Name) {
    return goals.slice().sort((goalA, goalB) => {
      const diff = goalA.name.localeCompare(goalB.name);
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Tasks) {
    return goals.slice().sort((goalA, goalB) => {
      const tasksA = goalA.taskCount ?? 0;
      const tasksB = goalB.taskCount ?? 0;
      const diff = tasksB - tasksA;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Impact) {
    const ranking: Record<ImpactLevel, number> = {
      [ImpactLevel.High]: 0,
      [ImpactLevel.Medium]: 1,
      [ImpactLevel.Low]: 2,
    };
    return goals.slice().sort((goalA, goalB) => {
      const rankA = goalA.impact != null ? ranking[goalA.impact] : 3;
      const rankB = goalB.impact != null ? ranking[goalB.impact] : 3;
      const diff = rankA - rankB;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Progress) {
    return goals.slice().sort((goalA, goalB) => {
      const goalACompletion = getGoalCompletion(goalA, { goals: allGoals });
      const goalBCompletion = getGoalCompletion(goalB, { goals: allGoals });

      if (goalBCompletion.end === Infinity) {
        return -1;
      }

      if (goalACompletion.end === Infinity) {
        return 1;
      }

      const completionPercentageA = getCompletionPercentage(goalACompletion);
      const completionPercentageB = getCompletionPercentage(goalBCompletion);

      const diff = completionPercentageB - completionPercentageA;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.LifeArea && lifeAreaOrder) {
    return goals.slice().sort((goalA, goalB) => {
      const orderA = goalA.lifeAreaId
        ? lifeAreaOrder.get(goalA.lifeAreaId) ?? Infinity
        : Infinity;
      const orderB = goalB.lifeAreaId
        ? lifeAreaOrder.get(goalB.lifeAreaId) ?? Infinity
        : Infinity;
      const diff = orderA - orderB;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  return goals;
};
